import {isNil} from "lodash";
import React, {Suspense, useMemo} from "react";
import {ParamTypes} from "../../constans/paramTypes";
import {UnitTypes} from "../../constans/unitTypes";
import useQueryCallerResource from "../../hooks/useQueryCallerResource";
import {useSQLChartFormatters} from "../../hooks/useSQLChartFormatters";
import {getParameterFormatter} from "../../utils/DashboardUtils";
import {getPercent} from "../../utils/GaugeUtils";
import {createQueryForGaugeComponent} from "../../utils/SQLUtils";
import {convertWeightToBaseUnit} from "../../utils/UnitUtils";
import Gauge from "../basics/gauge/Gauge";
import "./_sql-gauge-wrapper.scss";
import SQLNumber from "./SQLNumber";
import {Placeholder} from "react-bootstrap";
import SkeletonGauge from "../skeletons/SkeletonGauge";

function SQLGaugeWrapperRenderer({
    parameter,
    value,
    keysDictionary,
    translatedIDs,
}) {
    const formatter = useMemo(
        () => getParameterFormatter(parameter, keysDictionary),
        [parameter, keysDictionary]
    );

    const {min, max} = useMemo(() => {
        if (formatter !== ParamTypes.WEIGHT)
            return {min: parameter.min, max: parameter.max};
        if (parameter.min === undefined || parameter.max === undefined)
            return {min: 0, max: 0};
        return {
            min: convertWeightToBaseUnit(parameter.min, {
                fromUnit: UnitTypes.MEDIUM,
            }),
            max: convertWeightToBaseUnit(parameter.max, {
                fromUnit: UnitTypes.MEDIUM,
            }),
        };
    }, [parameter, formatter]);

    const getFormatter = useSQLChartFormatters("Gauge");

    const percent = useMemo(
        () => getPercent(value, min, max),
        [value, min, max]
    );

    const formattedValue = useMemo(() => {
        if (isNil(value)) return value;
        return +value.toFixed(2);
    }, [value]);

    console.log(formattedValue);

    return (
        <Gauge
            label={translatedIDs[parameter.id]}
            value={formattedValue}
            valueFormatter={getFormatter(formatter, {
                precision: parameter.precision,
            })}
            min={min}
            max={max}
            percent={percent}
            showLegend
            reverse={parameter.reverse}
        />
    );
}

function SQLNumberWrapperRenderer({
    parameter,
    value,
    keysDictionary,
    translatedIDs,
}) {
    const formatter = useMemo(
        () => getParameterFormatter(parameter, keysDictionary),
        [parameter, keysDictionary]
    );

    const getFormatter = useSQLChartFormatters("Number");

    return (
        <SQLNumber
            name={translatedIDs[parameter.id]}
            value={value}
            valueFormatter={getFormatter(formatter, {
                precision: parameter.precision,
            })}
        />
    );
}

function SQLGaugeAndNumberWrapperRenderer({
    resource,
    parameters,
    Type,
    maxRendered,
    keysDictionary,
}) {
    const data = resource.read();

    const Component = useMemo(() => {
        if (Type === "Gauge") return SQLGaugeWrapperRenderer;
        return SQLNumberWrapperRenderer;
    }, [Type]);

    const parametersToRender = useMemo(() => {
        return parameters.filter((item) => !!item.show);
    }, [parameters]);

    const translatedIDs = useMemo(() => {
        const tmp = {};
        for (let row of parameters) {
            tmp[row.id] = row.parameterName;
        }
        return tmp;
    }, [parameters]);

    return (
        <div className={`sql-gauge-wrapper ${Type}`}>
            {parametersToRender
                .slice(0, maxRendered)
                .map((parameter, index) => (
                    <Component
                        key={parameter.id}
                        parameter={parameter}
                        value={data[0]?.[parameter.id]}
                        keysDictionary={keysDictionary}
                        translatedIDs={translatedIDs}
                    />
                ))}
        </div>
    );
}

function SQLGaugeAndNumberSkeleton({amount, type}) {
    if (type === "Gauge") {
        return (
            <div className="sql-gauge-wrapper h-100">
                {[...Array(amount).keys()].map((item) => (
                    <Placeholder
                        key={item}
                        className="w-100 text-center h-100"
                        animation="glow">
                        <SkeletonGauge />
                        <Placeholder className="w-100" />
                    </Placeholder>
                ))}
            </div>
        );
    }
    return (
        <div className="sql-gauge-wrapper h-100">
            {[...Array(amount).keys()].map((item) => (
                <Placeholder
                    key={item}
                    className="w-100 text-center h-100"
                    animation="glow">
                    <Placeholder className="w-100" style={{height: "2rem"}} />
                    <Placeholder className="w-100" style={{height: ".8rem"}} />
                </Placeholder>
            ))}
        </div>
    );
}

export default function SQLGaugeAndNumberWrapper({
    Parameters,
    Filter,
    Options,
    filterValue,
    keysDictionary,
    filterItem,
    Type,
    maxRendered,
}) {
    const query = useMemo(() => {
        if (Parameters.length === 0) return null;
        return createQueryForGaugeComponent(
            Parameters,
            null,
            Filter,
            Options,
            filterItem,
            filterValue
        );
    }, [Parameters, Filter, Options, filterItem, filterValue]);

    const resource = useQueryCallerResource(query);

    return (
        <Suspense
            fallback={
                <div className="w-100 h-100" data-wait="true">
                    <SQLGaugeAndNumberSkeleton
                        amount={Parameters.length}
                        type={Type}
                    />
                </div>
            }>
            <SQLGaugeAndNumberWrapperRenderer
                Type={Type}
                maxRendered={maxRendered}
                keysDictionary={keysDictionary}
                parameters={Parameters}
                resource={resource}
            />
        </Suspense>
    );
}
